import { useCallback, useMemo, useState } from 'react';
import { WizardContainer } from '../../../components/wizard/Wizard';
import { useTranslation } from 'react-i18next';
import { WizardProvider } from '../../../components/wizard/useWizard';
import { AvailabilityAfterProgramStep } from './AvailabilityAfterProgramStep';
import { CharacterAndHabitsStep } from './CharacterAndHabitsStep';
import { InspirationAndWillingnessStep } from './InspirationAndWillingnessStep';
import { PracticeStep } from './PracticeStep';
import { SupportStep } from './SupportStep';
import { UnderstandingOfHeartfulnessStep } from './UnderstandingOfHeartfulnessStep';
import {
	OtherReferenceInterviewProvider,
	useOtherReferenceInterviewWizard,
} from './useOtherReferenceInterviewWizard';
import { useInterviewLandingPage } from '../InterviewLandingPage/useInterviewLandingPage';
import { OTHER_REFERENCE_CONTEXT } from '../../../shared/Routes';
import { OtherReferenceFeedback } from '../../../shared/VolunteerApplicationServiceTypes';

export const OtherReferenceInterviewWizard = () => {
	const { onSubmitClick } = useOtherReferenceInterviewWizard();

	const { t } = useTranslation();
	const { navigateToPage } = useInterviewLandingPage();
	const currentWizard = 0;
	const stepOffset = 0;
	const wizards = useMemo(
		() => [
			{
				steps: [
					<InspirationAndWillingnessStep key={2} />,
					<PracticeStep key={4} />,
					<CharacterAndHabitsStep key={5} />,
					<AvailabilityAfterProgramStep key={6} />,
					<SupportStep key={7} />,
					<UnderstandingOfHeartfulnessStep key={7} />,
				],
				descriptions: [
					{
						title: t('inspirationAndWillingness'),
						description: t('primaryQualificationForATrainer') ?? '',
					},
					{
						title: t('practice'),
						description: t('trainerNeedsToBeIntensePractitioner') ?? '',
					},
					{
						title: t('characterAndHabits'),
						description: t('expectedToBeRoleModel') ?? '',
					},
					{
						title: t('availabilityAfterProgram'),
						description: t('expectedToBeRoleModel') ?? '',
					},
					{
						title: t('familySupport'),
						description: t('exploreIfConditionsAroundAreConducive') ?? '',
					},
					{
						title: t('understandingHeartfulness'),
						description: t('abilityToExplainHeartfulness') ?? '',
					},
				],
			},
		],
		[t]
	);
	const steps = wizards[currentWizard].steps;
	const descriptions = wizards[currentWizard].descriptions;
	const [stepStates, setStepStates] = useState<number[]>(() => {
		const draftIndex = 0;
		return new Array(wizards.length)
			.fill(0)
			.map((_, index) =>
				index === currentWizard ? Math.max(0, draftIndex) : 0
			);
	});

	const showModal = useCallback(async (): Promise<boolean> => {
		return Promise.resolve(true);
	}, []);

	const customHandlePrevious = useCallback(() => {
		if (stepStates[currentWizard] == 0) {
			navigateToPage(OTHER_REFERENCE_CONTEXT);
		}
	}, [stepStates, navigateToPage]);

	const feedbackData: OtherReferenceFeedback = {
		review: '',
		comments: '',
	};

	return (
		<div className="flex flex-col h-full min-h-0 p-[8px]">
			<WizardContainer
				onSubmitClick={() => {
					onSubmitClick(feedbackData);
				}}
				onNextClick={showModal}
				onPreviousClick={customHandlePrevious}
				steps={steps}
				stepDescription={descriptions}
				stepOffset={stepOffset}
				initialStepState={stepStates[currentWizard] || 0}
				onStepStateChange={(newState: number) => {
					setStepStates((prev) => {
						const updated = [...prev];
						updated[currentWizard] = newState;
						return updated;
					});
				}}
				currentWizard={currentWizard}
			/>
		</div>
	);
};

export const OtherReferenceInterviewWizardContainer = () => {
	return (
		<OtherReferenceInterviewProvider>
			<WizardProvider>
				<OtherReferenceInterviewWizard />
			</WizardProvider>
		</OtherReferenceInterviewProvider>
	);
};
